// M4 balance (docs/M4_SPEC.md §17.3): what a producing shale well is worth in net worth. The M1 rule (120 days of
// today's gross output × 0.6) suits slow-declining conventional wells but values a decline-curve shale well at a
// fraction of its remaining reserves: a company that drills a hundred laterals saw its net worth fall while its
// production tripled. Wells with prod.shale (only ever created from 2000) are valued instead at the PV10 of their
// remaining type curve at today's realized netback (oil-value barrels, as the RBL borrowing base counts them), less
// lease operating cost, for at most 15 years — a flat-price "PDP PV10". Conventional wells keep the M1 rule.
import { netback, opexPerWell } from '../production.js';
import { yearOf } from '../defs.js';
import { curveRate } from './shaleProd.js';
import { oilValueFactor } from './marketFinance.js';

export const PDP_VALUE = { months: 180, discount: 0.10, haircut: 1.0 }; // haircut: optional discount to the PV10 (none: PDP trades near PV10)

const cache = new WeakMap(); // state → { day, n, v } (wells change at most once a day)

export function shaleWellValue(state, w, nbByField = new Map()) {
  const p = w.prod;
  if (!p?.shale || w.ownerOperatorId) return 0;
  let nb = nbByField.get(w.fieldId);
  if (!nb) { const x = netback(state, w.fieldId); nb = { cash: x.gross > 0 ? x.gross - x.royalty - x.severance - x.transport - x.region : 0 }; nbByField.set(w.fieldId, nb); }
  const perBoe = nb.cash * oilValueFactor(state, w);
  if (!(perBoe > 0)) return 0;
  const opexMonth = opexPerWell(yearOf(state)) * 30.44;
  const age = p.age ?? 0;
  let v = 0;
  for (let m = 0; m < PDP_VALUE.months; m++) {
    const q = curveRate(p, age + (m + 0.5) * 30.44);
    const cf = q * 30.44 * perBoe - opexMonth;
    if (cf <= 0) break;
    v += cf / Math.pow(1 + PDP_VALUE.discount, (m + 0.5) / 12);
  }
  return v * PDP_VALUE.haircut;
}

// Sum over the company's producing / shut-in shale wells (cached per day).
export function shaleAssetValue(state, wells) {
  const c = cache.get(state);
  if (c && c.day === state.day && c.n === wells.length) return c.v;
  const nbByField = new Map();
  let v = 0;
  for (const w of wells) if (w.prod?.shale && (w.status === 'PRODUCING' || w.status === 'SHUT_IN')) v += shaleWellValue(state, w, nbByField);
  cache.set(state, { day: state.day, n: wells.length, v });
  return v;
}
